// validation.js
// Centralized validation rules + helpers for the Procman AI Homepage CMS.
// Frontend/admin-panel only — no backend validation is added or assumed here.
//
// Limits below are derived from the actual homepage PDF layout (grid widths,
// number of lines a heading/description gets before wrapping badly, number of
// cards/icons the design has room for, etc.) so an admin editing content can't
// accidentally break the live page's layout.
//
// Everything is centralized here so the component never hardcodes a limit
// inline — it just looks up TEXT_RULES / REPEATER_RULES / IMAGE_SPECS by key.

// ---------------------------------------------------------------------------
// TEXT FIELD RULES
// Keyed by a dot-path that mirrors the content shape (see HomePageMaster.jsx).
// { min, max, required, help } — `help` is optional helper copy shown under
// the field when there's no error.
// ---------------------------------------------------------------------------
export const TEXT_RULES = {
  // ---- HERO ----
  "hero.section_badge": { min: 5, max: 40, required: true, help: "Small pill above the headline." },
  "hero.section_title": { min: 15, max: 50, required: true, help: "Main hero headline — keep to ~2 lines." },
  "hero.section_description": { min: 0, max: 150, required: false, help: "Optional line under the headline." },
  "hero.buyer_card.title": { min: 2, max: 25, required: true },
  "hero.buyer_card.cta.label": { min: 2, max: 20, required: true },
  "hero.buyer_card.cta.url": { type: "url", required: true },
  "hero.buyer_card.item.label": { min: 3, max: 30, required: true },
  "hero.supplier_card.title": { min: 2, max: 25, required: true },
  "hero.supplier_card.cta.label": { min: 2, max: 20, required: true },
  "hero.supplier_card.cta.url": { type: "url", required: true },
  "hero.supplier_card.item.label": { min: 3, max: 30, required: true },
  "hero.action.label": { min: 2, max: 20, required: true },
  "hero.action.url": { type: "url", required: true },
  "hero.action.variant": { min: 0, max: 20, required: false },

  // ---- TRUSTED BRANDS ----
  "trusted_brands.section_title": { min: 5, max: 30, required: true },
  "trusted_brands.section_description": { min: 0, max: 110, required: false },
  "trusted_brands.stat.number": { min: 1, max: 10, required: true, help: "e.g. 2,028+" },
  "trusted_brands.stat.label": { min: 2, max: 22, required: true },
  "trusted_brands.highlight.label": { min: 1, max: 22, required: true },

  // ---- HOW PROCMAN WORKS ----
  "working.section_title": { min: 5, max: 40, required: true },
  "working.step.title": { min: 2, max: 30, required: true },
  "working.step.description": { min: 0, max: 110, required: false },

  // ---- BUYER / SUPPLIER ----
  "buyer_supplier.section_title": { min: 5, max: 40, required: true },
  "buyer_supplier.section_description": { min: 0, max: 100, required: false },
  "buyer_supplier.buyer_card.title": { min: 2, max: 20, required: true },
  "buyer_supplier.buyer_card.cta.label": { min: 2, max: 22, required: true },
  "buyer_supplier.buyer_card.cta.url": { type: "url", required: true },
  "buyer_supplier.buyer_card.feature": { min: 3, max: 62, required: true },
  "buyer_supplier.supplier_card.title": { min: 2, max: 20, required: true },
  "buyer_supplier.supplier_card.cta.label": { min: 2, max: 22, required: true },
  "buyer_supplier.supplier_card.cta.url": { type: "url", required: true },
  "buyer_supplier.supplier_card.feature": { min: 3, max: 50, required: true },

  // ---- PLATFORM FEATURES ----
  "platform_features.section_badge": { min: 3, max: 20, required: true },
  "platform_features.section_title": { min: 5, max: 50, required: true },
  "platform_features.section_description": { min: 0, max: 80, required: false },
  "platform_features.feature.title": { min: 2, max: 35, required: true },
  "platform_features.feature.description": { min: 0, max: 110, required: false },

  // ---- COMPARISON (Why Procman) ----
  "comparision.section_badge": { min: 3, max: 15, required: true },
  "comparision.section_title": { min: 5, max: 60, required: true },
  "comparision.procman.title": { min: 2, max: 20, required: true },
  "comparision.procman.point": { min: 3, max: 60, required: true },
  "comparision.traditional.title": { min: 2, max: 20, required: true },
  "comparision.traditional.point": { min: 3, max: 60, required: true },

  // ---- ENTERPRISE SECURITY ----
  "enterprise_security.section_title": { min: 5, max: 50, required: true },
  "enterprise_security.feature.title": { min: 2, max: 20, required: true },
  "enterprise_security.feature.description": { min: 0, max: 140, required: false },

  // ---- INDUSTRIES ----
  "industries.section_title": { min: 5, max: 50, required: true },
  "industries.item.title": { min: 2, max: 22, required: true },
  "industries.item.description": { min: 0, max: 70, required: false },

  // ---- CASE STUDIES ----
  "case_studies.section_title": { min: 5, max: 50, required: true },
  "case_studies.item.title": { min: 5, max: 100, required: true },
  "case_studies.item.description": { min: 0, max: 160, required: false },

  // ---- TESTIMONIALS ----
  "testimonials.section_title": { min: 5, max: 50, required: true },
  "testimonials.item.name": { min: 2, max: 30, required: true },
  "testimonials.item.role": { min: 0, max: 40, required: false },
  "testimonials.item.quote": { min: 10, max: 220, required: true },

  // ---- FAQ ----
  "faq.section_title": { min: 5, max: 50, required: true },
  "faq.faq.question": { min: 5, max: 100, required: true },
  "faq.faq.answer": { min: 5, max: 320, required: true },

  // ---- CTA ----
  "cta.section_title": { min: 5, max: 60, required: true },
  "cta.section_description": { min: 0, max: 150, required: false },
  "cta.primary_cta.label": { min: 2, max: 22, required: true },
  "cta.primary_cta.url": { type: "url", required: true },
  "cta.secondary_cta.label": { min: 2, max: 22, required: true },
  "cta.secondary_cta.url": { type: "url", required: true },
  "cta.benefit.text": { min: 2, max: 30, required: true },
};

// ---------------------------------------------------------------------------
// REPEATER (array) COUNT RULES
// ---------------------------------------------------------------------------
export const REPEATER_RULES = {
  "hero.actions": { min: 1, max: 2, noun: "action button" },
  "hero.buyer_card.items": { min: 2, max: 6, noun: "item" },
  "hero.supplier_card.items": { min: 2, max: 6, noun: "item" },
  "trusted_brands.stats": { min: 2, max: 6, noun: "stat" },
  "trusted_brands.highlights": { min: 2, max: 8, noun: "highlight logo" },
  "working.steps": { min: 6, max: 9, noun: "step" },
  "buyer_supplier.buyer_card.features": { min: 2, max: 6, noun: "feature" },
  "buyer_supplier.supplier_card.features": { min: 2, max: 6, noun: "feature" },
  "platform_features.features": { min: 4, max: 8, noun: "feature" },
  "comparision.procman.points": { min: 3, max: 6, noun: "point" },
  "comparision.traditional.points": { min: 3, max: 6, noun: "point" },
  "enterprise_security.security_features": {
    min: 1,
    max: 3,
    noun: "security feature",
    help: "Capped at 3 — matches the 3 fixed image slots below it.",
  },
  "industries.items": { min: 4, max: 12, noun: "industry" },
  "case_studies.items": { min: 1, max: 6, noun: "case study" },
  "testimonials.items": { min: 2, max: 8, noun: "testimonial" },
  "faq.faqs": { min: 3, max: 12, noun: "FAQ" },
  "cta.benefits": { min: 0, max: 6, noun: "benefit" },
};

// ---------------------------------------------------------------------------
// IMAGE SPECS — format, size and dimension rules per image field.
// ---------------------------------------------------------------------------
const commonFormats = ["image/jpeg", "image/png", "image/webp"];

export const IMAGE_SPECS = {
 hero_background_image: {
  formats: commonFormats,
  maxSizeMB: 3,
  // minWidth: 2000, maxWidth: 2900, minHeight: 1130, maxHeight: 1650,
  aspect: 16 / 9, aspectTolerance: 0.05,
  help: "Landscape hero background. Recommended 2560×1440 (16:9), under 3MB.",
},
hero_desktop_dashboard_image: {
  formats: commonFormats,
  maxSizeMB: 2,
  minWidth: 1024, maxWidth: 2210, minHeight: 570, maxHeight: 1240,
  aspect: 16 / 9, aspectTolerance: 0.05,
  help: "Laptop dashboard mock. Recommended 1920×1080 (16:9), under 2MB.",
},
hero_mobile_dashboard_image: {
  formats: commonFormats,
  maxSizeMB: 1,
  // minWidth: 765, maxWidth: 1100, minHeight: 1573, maxHeight: 2328,
  // aspect: 9 / 18.5, aspectTolerance: 0.05,
  help: "Mobile dashboard mock. Recommended 900×1850 (9:18.5), under 1MB.",
},
buyer_card_image: {
  formats: ["image/webp"],
  maxSizeMB: 0.5,
  // minWidth: 1020, maxWidth: 1380, minHeight: 523, maxHeight: 707,
  aspect: 1.95, aspectTolerance: 0.05,
  help: "Buyer card image. Recommended 1200×615 (1.95:1), WebP only, under 500KB.",
},
supplier_card_image: {
  formats: ["image/webp"],
  maxSizeMB: 0.5,
  // minWidth: 1020, maxWidth: 1380, minHeight: 523, maxHeight: 707,
  aspect: 1.95, aspectTolerance: 0.05,
  help: "Supplier card image. Recommended 1200×615 (1.95:1), WebP only, under 500KB.",
},
  security_feature_1_image: {
    formats: commonFormats,
    maxSizeMB: 0.5,
    // minWidth: 800, maxWidth: 1200, minHeight: 550, maxHeight: 800,
    aspect: 3 / 2, aspectTolerance: 0.05,
    help: "Small square icon. ~240×240, under 500KB.",
  },
  security_feature_2_image: {
    formats: commonFormats,
    maxSizeMB: 0.5,
    // minWidth: 800, maxWidth: 1200, minHeight: 550, maxHeight: 800,
    aspect: 3 / 2, aspectTolerance: 0.05,
    help: "Small square icon. ~240×240, under 500KB.",
  },
  security_feature_3_image: {
    formats: commonFormats,
    maxSizeMB: 0.5,
    // minWidth: 800, maxWidth: 1200, minHeight: 550, maxHeight: 800,
    aspect: 3 / 2, aspectTolerance: 0.05,
    help: "Small square icon. ~240×240, under 500KB.",
  },
  og_image: {
    formats: commonFormats,
    // maxSizeMB: 1,
    // minWidth: 1100, maxWidth: 1300, minHeight: 580, maxHeight: 680,
    // aspect: 1200 / 630, aspectTolerance: 0.05,
    help: "Standard Open Graph size, 1200×630, under 1MB.",
  },
  twitter_image: {
    formats: commonFormats,
    // maxSizeMB: 1,
    // minWidth: 1100, maxWidth: 1300, minHeight: 625, maxHeight: 725,
    // aspect: 1200 / 675, aspectTolerance: 0.05,
    help: "Twitter card size, 1200×675, under 1MB.",
  },
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

/** Validate a plain text/textarea value against a TEXT_RULES entry. */
export function validateText(value, rules, label = "This field") {
  if (!rules) return null;
  const val = (value ?? "").toString();
  const len = val.trim().length;
  if (rules.required && len === 0) return `${label} is required.`;
  if (len === 0) return null; // optional + empty is fine
  if (rules.min && len < rules.min) return `${label} needs at least ${rules.min} characters (${len}/${rules.min}).`;
  if (rules.max && len > rules.max) return `${label} is over the limit (${len}/${rules.max}).`;
  return null;
}

/** Validate a URL — accepts an absolute https URL or a relative in-app path like /create-rfq. */
export function validateUrl(value, required = true, label = "URL") {
  const val = (value ?? "").trim();
  if (!val) return required ? `${label} is required.` : null;
  const relative = /^\/[a-zA-Z0-9\-_/?#=&.%]*$/;
  const absolute = /^https?:\/\/[^\s]+$/i;
  if (relative.test(val) || absolute.test(val)) return null;
  return `${label} must be a relative path (e.g. /create-rfq) or a full https:// URL.`;
}

/** Dispatches to validateText or validateUrl depending on the rules entry's `type`. */
export function validateByRules(value, rules, label) {
  if (!rules) return null;
  if (rules.type === "url") return validateUrl(value, rules.required, `${label} URL`);
  return validateText(value, rules, label);
}

/** Validate a repeater/array's item count against a REPEATER_RULES entry. */
export function validateRepeaterCount(items, rules) {
  if (!rules) return null;
  const count = items?.length || 0;
  if (rules.min && count < rules.min) {
    return `Add at least ${rules.min} ${rules.noun}${rules.min > 1 ? "s" : ""} (currently ${count}).`;
  }
  if (rules.max && count > rules.max) {
    return `No more than ${rules.max} ${rules.noun}${rules.max > 1 ? "s" : ""} allowed — remove ${count - rules.max} (currently ${count}).`;
  }
  return null;
}

/** Synchronous checks (format + file size) that don't require reading the image. */
export function validateImageFile(file, spec) {
  const errors = [];
  if (!spec.formats.includes(file.type)) {
    errors.push(`Unsupported format. Use ${spec.formats.map((f) => f.split("/")[1].toUpperCase()).join(", ")}.`);
  }
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > spec.maxSizeMB) {
    errors.push(`File is ${sizeMB.toFixed(1)}MB — max allowed is ${spec.maxSizeMB}MB.`);
  }
  return errors;
}

/** Reads natural width/height of an image file without uploading it anywhere. */
export function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

/** Dimension + aspect-ratio checks against a spec, given already-read {width,height}. */
export function validateImageDimensions({ width, height }, spec) {
  const errors = [];
  if (spec.minWidth && width < spec.minWidth) errors.push(`Width ${width}px is below the minimum ${spec.minWidth}px.`);
  if (spec.maxWidth && width > spec.maxWidth) errors.push(`Width ${width}px is above the maximum ${spec.maxWidth}px.`);
  if (spec.minHeight && height < spec.minHeight) errors.push(`Height ${height}px is below the minimum ${spec.minHeight}px.`);
  if (spec.maxHeight && height > spec.maxHeight) errors.push(`Height ${height}px is above the maximum ${spec.maxHeight}px.`);
  if (spec.aspect) {
    const ratio = width / height;
    const diff = Math.abs(ratio - spec.aspect) / spec.aspect;
    if (diff > (spec.aspectTolerance ?? 0.1)) {
      errors.push(`Aspect ratio (${ratio.toFixed(2)}:1) doesn't match the expected ~${spec.aspect.toFixed(2)}:1 — image may get cropped oddly.`);
    }
  }
  return errors;
}

/** Full check (format, size, dimensions) to run right before an upload is kicked off. */
export async function validateImageBeforeUpload(file, fieldName) {
  const spec = IMAGE_SPECS[fieldName];
  if (!spec) return [];
  const syncErrors = validateImageFile(file, spec);
  if (syncErrors.length) return syncErrors; // skip reading dimensions of an already-rejected file
  try {
    const dims = await getImageDimensions(file);
    return validateImageDimensions(dims, spec);
  } catch (e) {
    return [e.message || "Could not validate image dimensions."];
  }
}